import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  API_UNAUTHORIZED_EVENT,
  resetUnauthorizedGuard,
  type ApiUnauthorizedEventDetail,
} from '@/lib/api/client';
import { AUTH_QUERY_KEY, resetAuth } from '@/lib/auth/useAuth';

/**
 * AuthProvider wires the API client's 401 event to React Router navigation.
 *
 * Design:
 *   - No React context is needed: `useAuth()` already reads from TanStack
 *     Query, which is app-scoped. This component exists purely to attach
 *     navigation side-effects inside the Router tree (where `useNavigate`
 *     is valid).
 *   - On 401, we (a) mark the auth cache as unauthenticated so guards
 *     re-render, (b) navigate to `/login?next=<path>`, and (c) cancel the
 *     fallback `window.location.assign` in the client.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    function onUnauthorized(event: Event): void {
      const detail = (event as CustomEvent<ApiUnauthorizedEventDetail>).detail;
      const next = detail?.next ?? window.location.pathname + window.location.search;

      // Short-circuit the client's window.location fallback.
      event.preventDefault();

      resetAuth(queryClient);
      // Also invalidate so useAuth subscribers get the new (unauthenticated) value.
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });

      // Skip the redirect loop if already on the login page.
      const onLoginAlready = window.location.pathname === '/login';
      if (!onLoginAlready) {
        navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      }

      // Re-arm the guard so future 401s are handled (navigate is synchronous enough).
      resetUnauthorizedGuard();
    }

    window.addEventListener(API_UNAUTHORIZED_EVENT, onUnauthorized as EventListener);
    return () => {
      window.removeEventListener(API_UNAUTHORIZED_EVENT, onUnauthorized as EventListener);
    };
  }, [navigate, queryClient]);

  return <>{children}</>;
}
